import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import bowser from 'bowser';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';
import { combineStatuses } from 'resift';
import {
  useMsal,
  useIsAuthenticated,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../../../azureAuthConfig';

// Components
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Loader from 'components/Loader';
import SplitPageContainer from 'components/SplitPageContainer';
import SplitPageRight from 'components/SplitPageRight';
import SubscriptionSuccessIcon from 'components/Icons/SubscriptionSuccess';
import SubscriptionInfo from './SubscriptionInfo';

// Styles
import makeStyles from '@material-ui/core/styles/makeStyles';

function MicrosoftConfirmSubscription({
  location,
  status,
  subscriptionConfirmationStatus,
  subscriptionData,
  subscriptionConfirmationData,
  confirmSubscription,
  resolveSubscription,
}) {
  const classes = useStyles();
  const { instance, accounts, inProgress } = useMsal();
  const queryParams = queryString.parse(location.search);
  const subscriptionToken = _get(queryParams, 'token');
  const isAuthenticated = useIsAuthenticated();
  const [companyName, setCompanyName] = useState('');
  const pageIsLoading =
    status.isLoading() ||
    subscriptionConfirmationStatus.isLoading() ||
    inProgress !== InteractionStatus.None;
  const shouldRenderConfirmButton =
    _get(subscriptionData, ['siftSubscription', 'source']) !== 'database';

  useEffect(() => {
    document.title = 'Manage Your Sift Subscription';
  }, []);

  const handleChangeCompanyName = e => {
    setCompanyName(e.target.value);
  };

  const handleLogin = useCallback(
    () => {
      if (bowser.firefox || bowser.firefoxos) {
        instance.loginPopup(loginRequest).catch(e => {
          // eslint-disable-next-line no-console
          console.log(e);
        });
      } else {
        instance.loginRedirect(loginRequest).catch(e => {
          // eslint-disable-next-line no-console
          console.log(e);
        });
      }
    },
    [instance],
  );

  useEffect(
    () => {
      if (isAuthenticated && _isEmpty(subscriptionData) && inProgress === InteractionStatus.None) {
        instance
          .acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          })
          .then(response => {
            const { idToken } = response;
            idToken && subscriptionToken && resolveSubscription(idToken, subscriptionToken);
          })
          // eslint-disable-next-line no-console
          .catch(error => console.log(error));
      }
    },
    [
      isAuthenticated,
      subscriptionData,
      inProgress,
      accounts,
      instance,
      resolveSubscription,
      subscriptionToken,
    ],
  );

  const handleConfirm = useCallback(
    () => {
      instance
        .acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        })
        .then(response => {
          const { idToken } = response;
          confirmSubscription(
            idToken,
            subscriptionData.subscription.id,
            companyName.length ? companyName : null,
          );
        })
        // eslint-disable-next-line no-console
        .catch(error => console.log(error));
    },
    [accounts, companyName, instance, confirmSubscription, subscriptionData],
  );

  const title = shouldRenderConfirmButton ? (
    <Typography className={classes.header} variant="h2">
      Manage Your Sift Subscription
    </Typography>
  ) : (
    <>
      <SubscriptionSuccessIcon className={classes.icon} />
      <Typography className={classes.header} variant="h2">
        Success!
      </Typography>
    </>
  );

  return (
    <SplitPageContainer isMicrosoft>
      <SplitPageRight>
        <Loader
          className={classes.loader}
          data-testid="confirm-subscription-loader"
          status={combineStatuses(status.value, subscriptionConfirmationStatus.value)}
        >
          {title}
          <AuthenticatedTemplate>
            <SubscriptionInfo
              classes={classes}
              companyName={companyName}
              isLoading={pageIsLoading}
              subscriptionData={subscriptionData}
              subscriptionConfirmationData={subscriptionConfirmationData}
              onChangeCompanyName={handleChangeCompanyName}
              onConfirm={handleConfirm}
            />
          </AuthenticatedTemplate>
          <UnauthenticatedTemplate>
            <Button
              disabled={pageIsLoading}
              data-testid="login-button"
              onClick={handleLogin}
              color="secondary"
              variant="contained"
            >
              {pageIsLoading ? 'Loading' : 'Sign in with azure'}
            </Button>
          </UnauthenticatedTemplate>
        </Loader>
      </SplitPageRight>
    </SplitPageContainer>
  );
}

MicrosoftConfirmSubscription.propTypes = {
  status: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  subscriptionData: PropTypes.object.isRequired,
  subscriptionConfirmationData: PropTypes.object.isRequired,
  subscriptionConfirmationStatus: PropTypes.object.isRequired,
  confirmSubscription: PropTypes.func.isRequired,
  resolveSubscription: PropTypes.func.isRequired,
};

const useStyles = makeStyles(theme => {
  return {
    header: {
      color: theme.palette.primary.main,
      marginBottom: theme.spacing(5),
      [theme.breakpoints.down('xs')]: {
        marginTop: theme.spacing(4),
      },
    },
    error: {
      marginTop: theme.spacing(2),
      color: theme.palette.error.main,
      transition: 'opacity .3s',
      fontSize: 15,
    },
    inline: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(1.5),
    },
    loader: {
      position: 'sticky',
      height: '100%',
    },
    icon: {
      marginBottom: theme.spacing(2),
    },
  };
});

export default MicrosoftConfirmSubscription;
